<template>
  <transition :name="bgTransition">
    <div v-if="showWrapper" class="mtx-drawer" @click.self="hide" @keydown="onKeydown">
      <transition :name="transition" appear>
        <aside
          v-if="showing"
          ref="content"
          tabindex="-1"
          class="mtx-drawer__content px-2 py-2"
          :class="{ 'mtx-drawer__content--right': !!right }"
          :style="{
            width: width || false,
            maxWidth: maxWidth || false
          }"
        >
          <slot />
        </aside>
      </transition>
    </div>
  </transition>
</template>

<script>
import KEYCODES from "@/data/keycodes"
import FOCUSABLE from "@/data/focusable"
const NAME = "mtx-drawer"

/**
 * A sidebar that can be toggled on or off
 */
export default {
  name: NAME,

  model: {
    prop: "showing",
    event: "change"
  },

  props: {
    /**
     * @model
     */
    showing: Boolean,
    right: Boolean,
    width: String,
    maxWidth: String,
    preventScroll: Boolean,
    transition: String,
    bgTransition: String
  },

  data: () => ({
    showWrapper: false
  }),

  methods: {
    show() {
      /**
       * @event open
       * @type { null }
       */
      this.$emit("open")
      this.$emit("change", true)
    },

    hide() {
      /**
       * @event close
       * @type { null }
       */
      this.$emit("close")
      this.$emit("change", false)
    },

    toggle() {
      this.showing ? this.hide() : this.show()
    },

    onKeydown(event) {
      if (event.keyCode === KEYCODES.ESC) {
        this.hide()
      }

      if (event.keyCode === KEYCODES.TAB) {
        const content = this.$refs.content
        const focusable = Array.from(content.querySelectorAll(FOCUSABLE))
        const activeElement = document.activeElement

        if (focusable.length === 0) {
          return event.preventDefault()
        }

        if (this.showing && content && !content.contains(activeElement)) {
          focusable[0].focus()
          return event.preventDefault()
        }

        const focusedItemIndex = focusable.indexOf(activeElement)
        if (event.shiftKey && focusedItemIndex === 0) {
          focusable[focusable.length - 1].focus()
          return event.preventDefault()
        }

        if (!event.shiftKey && focusedItemIndex === focusable.length - 1) {
          focusable[0].focus()
          return event.preventDefault()
        }
      }
    }
  },

  watch: {
    showing: {
      async handler(next, prev) {
        if (next && next != prev) {
          this.preventScroll && document.body.style.setProperty("overflow", "hidden")
        } else {
          this.preventScroll && document.body.style.removeProperty("overflow")
        }

        await this.$nextTick()
        this.showWrapper = this.showing

        if (this.showing) {
          await this.$nextTick()
          this.$refs.content.focus()
        }
      }
    }
  }
}
</script>

<style lang="scss">
.mtx-drawer {
  position: fixed;
  z-index: 100;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);

  [tabindex="-1"]:focus {
    outline: 0;
  }

  &__content {
    overflow: auto;
    width: 100%;
    max-width: 300px;
    height: 100%;

    color: $offWhite;
    background: $offBlack;

    border-right: 5px solid var(--highlight);
    box-shadow: 0 0 50px -25px var(--highlight);
  }

  &__content--right {
    margin-left: auto;
    border-right: 0;
    border-left: 5px solid var(--highlight);
  }
}
</style>
